import React, { useState, useEffect } from 'react';
import { 
  Home, Trophy, Users, MessageSquare, Building2, Bell, User, LogOut, 
  ChevronLeft, ChevronRight, Menu, Settings, HelpCircle,
  ShieldCheck, UserCog, FolderKanban, DollarSign, X, Eye, Gift, Target, AlertTriangle, TrendingUp, XCircle,
  Sparkles, Briefcase, Newspaper, ChevronDown, Folder, ArrowLeft, BarChart, IndianRupee, ClipboardList, Gavel, CheckSquare
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserAvatar } from './UserAvatar';
import { api } from '@/utils/api';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

// BD Desk allowed roles
const BD_DESK_ALLOWED_ROLES = [
  'Super Admin', 'super_admin',
  'Business Head',
  'BD Ops',
  'Revenue BDM',
  'Strategy', 'strategy_support_admin',
  'Admin',
  'Manager'
];

const hasBDAccess = (user) => {
  return BD_DESK_ALLOWED_ROLES.includes(user?.role);
};

// Check if user is a Sales Rep
const isRepRole = (user) => {
  return user?.role === 'Rep' || user?.role === 'rep';
};

// Main navigation items for Sales Reps (redesigned hierarchy)
const REP_NAV_ITEMS = [
  { icon: Home, label: 'Home', path: '/' },
  { icon: Users, label: 'CRM', path: '/crm' },
  { icon: ClipboardList, label: 'My Tasks', path: '/my-tasks' },
  { icon: Trophy, label: 'Leaderboard', path: '/leaderboard' },
  { icon: Newspaper, label: 'Market Intel', path: '/market-intelligence' },
  { icon: MessageSquare, label: 'Feed', path: '/feed' },
  { icon: Sparkles, label: 'SAIRA', path: '/saira' },
];

// Tools section for Reps (collapsible)
const REP_TOOLS_ITEMS = [
  { icon: Building2, label: 'Projects', path: '/projects' },
];

// Admin/Manager navigation items (unchanged)
const ADMIN_NAV_ITEMS = [
  { icon: Home, label: 'Home', path: '/' },
  { icon: Users, label: 'CRM', path: '/crm' },
  { icon: Sparkles, label: 'SAIRA', path: '/saira' },
  { icon: Newspaper, label: 'Market Intel', path: '/market-intelligence' },
  { icon: MessageSquare, label: 'Feed', path: '/feed' },
  { icon: Trophy, label: 'Leaderboard', path: '/leaderboard' },
  { icon: Building2, label: 'Projects', path: '/projects' },
];

// CRM Support specific navigation - simplified view focused on booking operations
const CRM_SUPPORT_NAV_ITEMS = [
  { icon: Home, label: 'Home', path: '/' },
  { icon: ClipboardList, label: 'Booking Journey', path: '/city-head-dashboard?tab=bookings' },
  { icon: XCircle, label: 'Cancellation History', path: '/admin/cancellation-history' },
  { icon: CheckSquare, label: 'Tasks', path: '/my-tasks' },
];

// Admin-only menu items - Updated for governance model
// CLEANED: Removed legacy dashboard and booking funnel (merged into Sales Intelligence)
// SIMPLIFIED: Booking Verification hidden - All Bookings in Sales Intelligence is now the single booking operations screen
const ADMIN_MENU_ITEMS = [
  { icon: Target, label: 'Command Center', path: '/admin/command-center', roles: ['super_admin', 'strategy_support_admin'] },
  { icon: BarChart, label: 'Sales Intelligence', path: '/city-head-dashboard', roles: ['super_admin', 'strategy_support_admin', 'city_admin', 'Manager'] },
  { icon: ClipboardList, label: 'Manager Desk', path: '/manager-desk', roles: ['super_admin', 'strategy_support_admin', 'city_admin', 'Manager'] },
  { icon: Gavel, label: 'Auction Supply', path: '/admin/supply/auctions', roles: ['super_admin', 'Admin', 'strategy_support_admin'], section: 'Supply' },
  { icon: AlertTriangle, label: 'VDNB Management', path: '/admin/vdnb', roles: ['super_admin', 'city_admin', 'strategy_support_admin'] },
  { icon: UserCog, label: 'User Management', path: '/admin/users', roles: ['super_admin', 'city_admin'] },
  { icon: FolderKanban, label: 'Project Management', path: '/admin/projects', roles: ['super_admin', 'strategy_support_admin', 'city_admin'] },
  // Booking Verification removed from nav - use Sales Intelligence → All Bookings instead
  { icon: XCircle, label: 'Cancellation History', path: '/admin/cancellation-history', roles: ['super_admin', 'strategy_support_admin', 'city_admin'] },
  { icon: Gift, label: 'Referral Dashboard', path: '/admin/referrals', roles: ['super_admin', 'strategy_support_admin', 'city_admin'] },
  { icon: DollarSign, label: 'Incentive Slabs', path: '/admin/slabs', roles: ['super_admin'] },
];

// Check if user has admin role (updated for new governance)
// Includes Manager role since they have access to some admin tools (Sales Intelligence, Manager Desk)
// Includes CRM Support role for CRM-related admin functions
const isAdmin = (user) => {
  return ['super_admin', 'strategy_support_admin', 'city_admin', 'Admin', 'Manager', 'crm_support'].includes(user?.role);
};

// Check if path is active
const isPathActive = (currentPath, itemPath) => {
  if (itemPath === '/') {
    return currentPath === '/';
  }
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
};

// Get role display name - Updated for new governance
const getRoleDisplayName = (role) => {
  const roleMap = {
    'super_admin': 'Super Admin',
    'strategy_support_admin': 'Strategy Support',
    'crm_support': 'CRM Support',
    'city_admin': 'City Admin',
    'Manager': 'Manager',
    'Rep': 'Sales Rep',
    'rep': 'Sales Rep',
    'crm_support': 'CRM Support',
    'Admin': 'Admin (Legacy)'
  };
  return roleMap[role] || role;
};

// Notification Bell Component
const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.getNotifications({ limit: 10 });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.getNotificationCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch notification count');
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        await api.markNotificationRead(notification.notification_id);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(notifications.map(n => 
          n.notification_id === notification.notification_id 
            ? { ...n, read: true } 
            : n
        ));
      } catch (error) {
        console.error('Failed to mark notification as read');
      }
    }
    
    if (notification.post_id) {
      navigate('/feed');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          data-testid="notification-bell"
          className="relative p-2 rounded-full hover:bg-slate-100 transition-colors duration-200"
        >
          <Bell size={20} className="text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#5B3DF5] rounded-full flex items-center justify-center animate-in fade-in duration-200">
              <span className="text-[10px] font-bold text-white">{unreadCount > 9 ? '9+' : unreadCount}</span>
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <span className="font-semibold text-sm text-slate-700">Notifications</span>
          {unreadCount > 0 && (
            <button 
              onClick={handleMarkAllRead}
              className="text-xs text-blue-600 hover:text-blue-700 transition-colors"
            >
              Mark all read
            </button>
          )}
        </div>
        {notifications.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-slate-400">
            No notifications yet
          </div>
        ) : (
          notifications.map((notification) => (
            <DropdownMenuItem
              key={notification.notification_id}
              onClick={() => handleNotificationClick(notification)}
              className={`flex flex-col items-start gap-1 px-3 py-2.5 cursor-pointer transition-colors duration-150 ${
                !notification.read ? 'bg-blue-50/50' : ''
              }`}
            >
              <div className="flex items-start gap-2 w-full">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 transition-all duration-200 ${
                  !notification.read ? 'bg-blue-500' : 'bg-transparent'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700">{notification.title}</p>
                  <p className="text-xs text-slate-500 truncate">{notification.message}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{formatTime(notification.created_at)}</p>
                </div>
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Mobile Hamburger Menu Sheet
const MobileMenuSheet = ({ user, isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userIsAdmin = isAdmin(user);
  const userIsRep = isRepRole(user);

  const handleNavigation = (path) => {
    navigate(path);
    onClose?.();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    onClose?.();
  };

  // Determine navigation items based on user role
  const isCrmSupport = user?.role === 'crm_support';
  const navItems = userIsRep ? REP_NAV_ITEMS : (isCrmSupport ? CRM_SUPPORT_NAV_ITEMS : ADMIN_NAV_ITEMS);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[280px] p-0 flex flex-col">
        <SheetHeader className="sr-only">
          <SheetTitle>Navigation Menu</SheetTitle>
        </SheetHeader>
        
        {/* User Profile Header */}
        <div className="px-5 py-6 bg-gradient-to-br from-[#5B3DF5] to-[#8B5CF6]">
          <div className="flex items-center gap-3">
            <UserAvatar 
              name={user?.rep_name || user?.email}
              imageUrl={user?.profile_image_url}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white truncate">{user?.rep_name || user?.email?.split('@')[0]}</p>
              <p className="text-xs text-white/70">{getRoleDisplayName(user?.role)}</p>
              {user?.city && <p className="text-xs text-white/60">{user.city}</p>}
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isPathActive(location.pathname, item.path);
              
              return (
                <button
                  key={item.label}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    active 
                      ? 'bg-[#F3F0FF] text-[#5B3DF5]' 
                      : 'text-slate-700 hover:bg-slate-100 active:scale-[0.98]'
                  }`}
                >
                  <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                  <span className={`text-sm ${active ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tools Section for Reps */}
          {userIsRep && (
            <>
              <div className="my-4 mx-5 border-t border-slate-100" />
              <div className="px-5 py-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tools</p>
              </div>
              <div className="space-y-1 px-3">
                {REP_TOOLS_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const active = isPathActive(location.pathname, item.path);
                  
                  return (
                    <button
                      key={item.label}
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                        active 
                          ? 'bg-[#F3F0FF] text-[#5B3DF5]' 
                          : 'text-slate-700 hover:bg-slate-100 active:scale-[0.98]'
                      }`}
                    >
                      <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                      <span className={`text-sm ${active ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* BD Desk Section */}
          {hasBDAccess(user) && (
            <>
              <div className="my-4 mx-5 border-t border-slate-100" />
              <div className="px-5 py-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Business Development</p>
              </div>
              <div className="px-3">
                <button
                  onClick={() => handleNavigation('/bd')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    location.pathname.startsWith('/bd') 
                      ? 'bg-[#F3F0FF] text-[#5B3DF5]' 
                      : 'text-slate-700 hover:bg-slate-100 active:scale-[0.98]'
                  }`}
                >
                  <Briefcase size={20} />
                  <span className="text-sm font-medium">BD Desk</span>
                </button>
              </div>
            </>
          )}

          {/* Admin Section - Hidden for CRM_SUPPORT (they have simplified nav) */}
          {userIsAdmin && user?.role !== 'crm_support' && (
            <>
              <div className="my-4 mx-5 border-t border-slate-100" />
              <div className="px-5 py-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Admin Tools</p>
              </div>
              <div className="space-y-1 px-3">
                {ADMIN_MENU_ITEMS.filter(item => item.roles.includes(user?.role)).map((item) => {
                  const Icon = item.icon;
                  const active = isPathActive(location.pathname, item.path);
                  
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                        active 
                          ? 'bg-[#F3F0FF] text-[#5B3DF5]' 
                          : 'text-slate-700 hover:bg-slate-100 active:scale-[0.98]'
                      }`}
                    >
                      <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                      <span className={`text-sm ${active ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Profile Link */}
          <div className="my-4 mx-5 border-t border-slate-100" />
          <div className="px-3">
            <button
              onClick={() => handleNavigation('/profile')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                location.pathname === '/profile' 
                  ? 'bg-[#F3F0FF] text-[#5B3DF5]' 
                  : 'text-slate-700 hover:bg-slate-100 active:scale-[0.98]'
              }`}
            >
              <User size={20} />
              <span className="text-sm font-medium">Profile</span>
            </button>
          </div>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-slate-100">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 hover:bg-red-50 text-slate-700 hover:text-red-600 rounded-xl transition-all duration-200 active:scale-[0.98]"
            data-testid="menu-logout-btn"
          >
            <LogOut size={18} />
            <span className="font-semibold text-sm">Logout</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Desktop Sidebar Navigation (unchanged for desktop)
export const Sidebar = ({ user, collapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userIsAdmin = isAdmin(user);
  const userIsRep = isRepRole(user);
  const [toolsOpen, setToolsOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Choose nav items based on role
  const isCrmSupport = user?.role === 'crm_support';
  const mainNavItems = userIsRep ? REP_NAV_ITEMS : (isCrmSupport ? CRM_SUPPORT_NAV_ITEMS : ADMIN_NAV_ITEMS);

  return (
    <aside 
      className={`hidden md:flex flex-col fixed left-0 top-0 h-screen bg-white border-r border-slate-200 transition-all duration-300 z-50 ${
        collapsed ? 'w-[72px]' : 'w-[220px]'
      }`}
    >
      {/* Logo / Brand - Purple */}
      <div className={`flex items-center h-16 px-4 border-b border-slate-100 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#5B3DF5] to-[#4A2FE0] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">PX</span>
            </div>
            <span className="font-bold text-[#111827]">Pipeline X</span>
          </div>
        )}
        {collapsed && (
          <div className="w-8 h-8 bg-gradient-to-br from-[#5B3DF5] to-[#4A2FE0] rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">PX</span>
          </div>
        )}
        <button 
          onClick={onToggle}
          className={`p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors ${collapsed ? 'hidden' : ''}`}
          data-testid="sidebar-collapse-btn"
        >
          <ChevronLeft size={18} />
        </button>
      </div>

      {/* Expand button when collapsed */}
      {collapsed && (
        <button 
          onClick={onToggle}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm hover:bg-slate-50 transition-colors"
          data-testid="sidebar-expand-btn"
        >
          <ChevronRight size={14} className="text-slate-500" />
        </button>
      )}

      {/* Main Navigation Items */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {mainNavItems.map((item) => {
          const Icon = item.icon;
          const active = isPathActive(location.pathname, item.path);
          
          return (
            <button
              key={item.label}
              data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                active 
                  ? 'bg-[#F3F0FF] text-[#5B3DF5]' 
                  : 'text-slate-600 hover:bg-slate-100'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon 
                size={20} 
                strokeWidth={active ? 2.5 : 2} 
                className={`flex-shrink-0 transition-transform duration-200 ${active ? 'scale-105' : 'group-hover:scale-105'}`}
              />
              {!collapsed && (
                <span className={`text-sm ${active ? 'font-semibold' : 'font-medium'}`}>
                  {item.label}
                </span>
              )}
              {active && !collapsed && (
                <div className="ml-auto w-1.5 h-1.5 bg-[#5B3DF5] rounded-full" />
              )}
            </button>
          );
        })}

        {/* Tools Section for Reps (Collapsible) */}
        {userIsRep && !collapsed && (
          <>
            <div className="my-3 border-t border-slate-100" />
            <Collapsible open={toolsOpen} onOpenChange={setToolsOpen}>
              <CollapsibleTrigger asChild>
                <button className="w-full flex items-center gap-3 px-3 py-2 text-slate-500 hover:text-slate-700 transition-colors">
                  <Folder size={16} />
                  <span className="text-xs font-semibold uppercase tracking-wider">Tools</span>
                  <ChevronDown 
                    size={14} 
                    className={`ml-auto transition-transform duration-200 ${toolsOpen ? 'rotate-180' : ''}`}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 mt-1">
                {REP_TOOLS_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const active = isPathActive(location.pathname, item.path);
                  
                  return (
                    <button
                      key={item.label}
                      data-testid={`sidebar-tool-${item.label.toLowerCase()}`}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 px-3 py-2 pl-6 rounded-xl transition-all duration-200 ${
                        active 
                          ? 'bg-[#F3F0FF] text-[#5B3DF5]' 
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Icon size={18} strokeWidth={active ? 2.5 : 2} />
                      <span className={`text-sm ${active ? 'font-semibold' : 'font-medium'}`}>
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          </>
        )}

        {/* BD Desk Section - Role-based visibility */}
        {hasBDAccess(user) && (
          <>
            <div className={`my-3 border-t border-slate-100 ${collapsed ? 'mx-2' : ''}`} />
            {!collapsed && (
              <div className="px-3 py-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">BD Desk</p>
              </div>
            )}
            
            <button
              data-testid="sidebar-nav-bd-desk"
              onClick={() => navigate('/bd')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                isPathActive(location.pathname, '/bd') 
                  ? 'bg-[#F3F0FF] text-[#5B3DF5]' 
                  : 'text-slate-600 hover:bg-slate-100'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? 'BD Desk' : undefined}
            >
              <Briefcase 
                size={20} 
                strokeWidth={isPathActive(location.pathname, '/bd') ? 2.5 : 2}
                className="flex-shrink-0"
              />
              {!collapsed && (
                <span className={`text-sm ${isPathActive(location.pathname, '/bd') ? 'font-semibold' : 'font-medium'}`}>
                  BD Desk
                </span>
              )}
            </button>

            <button
              data-testid="sidebar-nav-bd-dashboard"
              onClick={() => navigate('/bd-dashboard')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                isPathActive(location.pathname, '/bd-dashboard') 
                  ? 'bg-[#F3F0FF] text-[#5B3DF5]' 
                  : 'text-slate-600 hover:bg-slate-100'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? 'Revenue Tracker' : undefined}
            >
              <IndianRupee 
                size={20} 
                strokeWidth={isPathActive(location.pathname, '/bd-dashboard') ? 2.5 : 2}
                className="flex-shrink-0"
              />
              {!collapsed && (
                <span className={`text-sm ${isPathActive(location.pathname, '/bd-dashboard') ? 'font-semibold' : 'font-medium'}`}>
                  Revenue Tracker
                </span>
              )}
            </button>
          </>
        )}

        {/* Admin Section - Hidden for CRM_SUPPORT (they have simplified nav) */}
        {userIsAdmin && user?.role !== 'crm_support' && (
          <>
            <div className={`my-3 border-t border-slate-100 ${collapsed ? 'mx-2' : ''}`} />
            {!collapsed && (
              <div className="px-3 py-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Admin</p>
              </div>
            )}
            
            {ADMIN_MENU_ITEMS.filter(item => item.roles.includes(user?.role)).map((item) => {
              const Icon = item.icon;
              const active = isPathActive(location.pathname, item.path);
              
              return (
                <button
                  key={item.path}
                  data-testid={`sidebar-admin-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                    active 
                      ? 'bg-[#F3F0FF] text-[#5B3DF5]' 
                      : 'text-slate-600 hover:bg-slate-100'
                  } ${collapsed ? 'justify-center' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon 
                    size={20} 
                    strokeWidth={active ? 2.5 : 2}
                    className="flex-shrink-0"
                  />
                  {!collapsed && (
                    <span className={`text-sm ${active ? 'font-semibold' : 'font-medium'}`}>
                      {item.label}
                    </span>
                  )}
                </button>
              );
            })}
          </>
        )}
      </nav>

      {/* User Profile & Logout */}
      <div className={`border-t border-slate-100 p-3 ${collapsed ? 'flex flex-col items-center gap-2' : ''}`}>
        {collapsed ? (
          <>
            <button
              onClick={() => navigate('/profile')}
              className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
              title="Profile"
            >
              <UserAvatar 
                name={user?.rep_name || user?.email}
                imageUrl={user?.profile_image_url}
                size="sm"
              />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 rounded-xl hover:bg-red-50 text-slate-500 hover:text-[#5B3DF5] transition-colors"
              title="Logout"
              data-testid="sidebar-logout-btn"
            >
              <LogOut size={18} />
            </button>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-3 flex-1 p-2 rounded-xl hover:bg-slate-100 transition-colors"
            >
              <UserAvatar 
                name={user?.rep_name || user?.email}
                imageUrl={user?.profile_image_url}
                size="sm"
              />
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-[#111827] truncate">
                  {user?.rep_name || user?.email?.split('@')[0]}
                </p>
                <p className="text-xs text-slate-500 truncate">{getRoleDisplayName(user?.role)}</p>
              </div>
            </button>
            <button
              onClick={handleLogout}
              className="p-2 rounded-xl hover:bg-red-50 text-slate-500 hover:text-[#5B3DF5] transition-colors"
              title="Logout"
              data-testid="sidebar-logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

// Admin Mode Chip Component
export const AdminModeChip = ({ user, onSwitchToRepView }) => {
  if (!isAdmin(user)) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-100 border border-purple-200 rounded-full">
      <span className="text-xs font-semibold text-purple-700">
        Mode: {getRoleDisplayName(user?.role)}
      </span>
      {onSwitchToRepView && (
        <button
          onClick={onSwitchToRepView}
          className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium"
        >
          <Eye size={12} />
          Rep View
        </button>
      )}
    </div>
  );
};

// TopBar Component (for page headers) - Now with back button and hamburger
export const TopBar = ({ title, subtitle, lastSync, user, showAvatar = false, showNotifications = false, showAdminChip = false, showBack = false, onBack }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="bg-white border-b border-slate-100 px-4 py-4 md:px-8">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-3">
          {/* Mobile Hamburger Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 -ml-2 rounded-full hover:bg-slate-100 transition-colors duration-200 md:hidden"
            data-testid="hamburger-menu-btn"
          >
            <Menu size={22} className="text-slate-700" />
          </button>
          {showBack && (
            <button
              onClick={handleBack}
              className="p-2 -ml-2 rounded-full hover:bg-slate-100 transition-colors duration-200 md:hidden"
              data-testid="topbar-back-btn"
            >
              <ArrowLeft size={20} className="text-slate-600" />
            </button>
          )}
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-[#111827]">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
            )}
            {lastSync && (
              <p className="text-xs text-slate-400 mt-2">
                Last sync: {new Date(lastSync).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showAdminChip && <AdminModeChip user={user} />}
          {showNotifications && <NotificationBell />}
          {showAvatar && (
            <UserAvatar 
              name={user?.rep_name || user?.email}
              imageUrl={user?.profile_image_url}
              size="md"
            />
          )}
          <div className="text-right hidden md:block">
            <p className="text-sm font-medium text-[#111827]">{user?.rep_name || user?.email}</p>
            <p className="text-xs text-slate-500">{getRoleDisplayName(user?.role)}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="md:hidden text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
      
      {/* Mobile Menu Sheet */}
      <MobileMenuSheet 
        user={user} 
        isOpen={mobileMenuOpen} 
        onClose={() => setMobileMenuOpen(false)} 
      />
    </div>
  );
};

// Floating SAIRA Button for Reps - Repositioned for no bottom nav
export const FloatingSairaButton = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userIsRep = isRepRole(user);

  // Don't show on SAIRA page itself or for non-reps
  if (!userIsRep || location.pathname === '/saira') return null;

  return (
    <button
      onClick={() => navigate('/saira')}
      className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-[#5B3DF5] to-[#8B5CF6] rounded-full shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 transition-transform duration-200 z-[999998]"
      data-testid="floating-saira-btn"
      style={{ boxShadow: '0 4px 20px rgba(91, 61, 245, 0.4)' }}
    >
      <Sparkles size={24} className="text-white" />
    </button>
  );
};

// Main Layout Wrapper with Sidebar and Hamburger menu
export const AppLayout = ({ children, user }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const newState = !prev;
      localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
      return newState;
    });
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      {/* Desktop Sidebar */}
      <Sidebar 
        user={user} 
        collapsed={sidebarCollapsed} 
        onToggle={toggleSidebar} 
      />
      
      {/* Main Content Area - NO bottom padding on mobile anymore */}
      <main className={`transition-all duration-300 ${
        sidebarCollapsed ? 'md:ml-[72px]' : 'md:ml-[220px]'
      }`}>
        {children}
      </main>
      
      {/* Mobile Hamburger Menu */}
      <MobileMenuSheet 
        user={user} 
        isOpen={mobileMenuOpen} 
        onClose={() => setMobileMenuOpen(false)} 
      />
      
      {/* Floating SAIRA Button for Reps */}
      <FloatingSairaButton user={user} />
    </div>
  );
};

// Export mobile menu trigger for use in headers
export const MobileMenuTrigger = ({ user, className = "" }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  
  return (
    <>
      <button
        onClick={() => setMenuOpen(true)}
        className={`md:hidden p-2 rounded-full hover:bg-slate-100 transition-colors duration-200 ${className}`}
        data-testid="hamburger-menu-btn"
      >
        <Menu size={22} className="text-slate-700" />
      </button>
      <MobileMenuSheet 
        user={user} 
        isOpen={menuOpen} 
        onClose={() => setMenuOpen(false)} 
      />
    </>
  );
};

// Utility exports
export { isAdmin, getRoleDisplayName, isRepRole };

// DEPRECATED: BottomNav removed in mobile redesign - stub for backward compatibility
export const BottomNav = () => null;
